import { supabase } from '../lib/supabase';
import { CheckoutSessionRequestSchema } from '@/lib/schemas';
import * as Sentry from '@sentry/react';

export type PlanId = 'free' | 'starter' | 'business';

export interface PlanLimits {
    bots: number;
    documents: number;
    charsPerDocument: number;
    totalChars: number;
    scrapesPerMonth: number;
}

export interface UserSubscription {
    planId: PlanId;
    active: boolean;
    botLimit: number;
    limits: PlanLimits;
}

// Plan limits configuration
export const PLAN_LIMITS: Record<PlanId, PlanLimits> = {
    free: {
        bots: 0,
        documents: 0,
        charsPerDocument: 0,
        totalChars: 0,
        scrapesPerMonth: 0
    },
    starter: {
        bots: 1,
        documents: 5,
        charsPerDocument: 50000,    // ~50K chars per doc
        totalChars: 100000,         // ~100K total
        scrapesPerMonth: 0          // Website extraction is Business plan only
    },
    business: {
        bots: 10,
        documents: 50,
        charsPerDocument: 200000,   // ~200K chars per doc
        totalChars: 2000000,        // ~2M total
        scrapesPerMonth: 50         // 50 scrapes per month
    }
};

export const subscriptionService = {
    getUserSubscription: async (): Promise<UserSubscription> => {
        // Local bypass for testing (only in DEV)
        const bypassStripe = import.meta.env.VITE_BYPASS_STRIPE === 'true' || import.meta.env.VITE_BYPASS_STRIPE === true;
        if (import.meta.env.DEV && bypassStripe) {
            return {
                planId: 'business',
                active: true,
                botLimit: PLAN_LIMITS.business.bots,
                limits: PLAN_LIMITS.business
            };
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            // Local Mode - Unrestricted Access
            return {
                planId: 'business',
                active: true,
                botLimit: 100,
                limits: { bots: 100, documents: 100, charsPerDocument: 500000, totalChars: 10000000, scrapesPerMonth: 1000 }
            };
        }

        try {
            if (import.meta.env.DEV) console.log('[subscriptionService] Querying subscriptions table for user:', user.id);
            const { data: sub, error } = await supabase
                .from('subscriptions')
                .select('plan_id, status, scrapes_used_this_month, scrapes_reset_at')
                .eq('user_id', user.id)
                .maybeSingle();  // Get the row regardless of status

            if (import.meta.env.DEV) console.log('[subscriptionService] Raw DB result:', { sub, error });

            if (error) {
                Sentry.captureException(error, { extra: { action: 'getUserSubscription' } });
                return { planId: 'free', active: false, botLimit: 0, limits: PLAN_LIMITS.free };
            }

            // If no row or status is inactive or plan_id is null, return free
            if (!sub || sub.status !== 'active' || !sub.plan_id) {
                if (import.meta.env.DEV) console.log('[subscriptionService] No active subscription found, returning free plan');
                return { planId: 'free', active: false, botLimit: 0, limits: PLAN_LIMITS.free };
            }

            const planId = sub.plan_id as PlanId;
            const limits = PLAN_LIMITS[planId] || PLAN_LIMITS.free;
            const result = {
                planId,
                active: true,
                botLimit: limits.bots,
                limits
            };
            if (import.meta.env.DEV) console.log('[subscriptionService] Subscription found:', result);
            return result;
        } catch (err) {
            Sentry.captureException(err, { extra: { action: 'getUserSubscription' } });
            return { planId: 'free', active: false, botLimit: 0, limits: PLAN_LIMITS.free };
        }
    },

    getPlanLimits: (planId: PlanId): PlanLimits => {
        return PLAN_LIMITS[planId] || PLAN_LIMITS.free;
    },

    getBotLimit: (planId: PlanId): number => {
        return PLAN_LIMITS[planId]?.bots || 0;
    },

    canCreateBot: async (currentBotCount: number): Promise<{ allowed: boolean; reason?: string }> => {
        const sub = await subscriptionService.getUserSubscription();

        if (!sub.active) {
            return { allowed: false, reason: 'Please subscribe to a plan to create bots.' };
        }

        if (currentBotCount >= sub.limits.bots) {
            return {
                allowed: false,
                reason: `You've reached your limit of ${sub.limits.bots} bots for the ${sub.planId} plan.`
            };
        }

        return { allowed: true };
    },

    canCreateDocument: async (currentDocCount: number, currentTotalChars: number): Promise<{ allowed: boolean; reason?: string }> => {
        const sub = await subscriptionService.getUserSubscription();

        if (!sub.active) {
            return { allowed: false, reason: 'Please subscribe to a plan to add documents.' };
        }

        if (currentDocCount >= sub.limits.documents) {
            return {
                allowed: false,
                reason: `You've reached your limit of ${sub.limits.documents} documents for the ${sub.planId} plan. Upgrade to add more.`
            };
        }

        if (currentTotalChars >= sub.limits.totalChars) {
            return {
                allowed: false,
                reason: `You've reached your storage limit of ${Math.round(sub.limits.totalChars / 1000)}K characters for the ${sub.planId} plan. Upgrade for more storage.`
            };
        }

        return { allowed: true };
    },

    validateDocumentSize: async (charCount: number): Promise<{ allowed: boolean; reason?: string }> => {
        const sub = await subscriptionService.getUserSubscription();

        if (!sub.active) {
            return { allowed: false, reason: 'Please subscribe to a plan to add documents.' };
        }

        if (charCount > sub.limits.charsPerDocument) {
            return {
                allowed: false,
                reason: `Document exceeds the ${Math.round(sub.limits.charsPerDocument / 1000)}K character limit for the ${sub.planId} plan. Try uploading a smaller file or upgrade your plan.`
            };
        }

        return { allowed: true };
    },

    // flow: 'update' deep-links the Stripe portal to the plan-switch screen
    createPortalSession: async (flow?: 'update'): Promise<string> => {
        // Refresh session first to ensure JWT is fresh (avoids 401 from gateway)
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
            Sentry.addBreadcrumb({ category: 'auth', message: 'Session refresh failed before portal', data: { error: refreshError.message } });
        }

        const { data, error } = await supabase.functions.invoke('create-portal-session', { body: { flow } });

        if (error) {
            Sentry.captureException(error, { extra: { action: 'createPortalSession' } });

            const errorMessage = error.message || '';

            if (errorMessage.includes('401') || errorMessage.toLowerCase().includes('unauthorized')) {
                // Retry after session refresh
                const { error: retryRefreshError } = await supabase.auth.refreshSession();
                if (retryRefreshError) {
                    Sentry.captureException(retryRefreshError, { extra: { action: 'createPortalSession.retryRefresh' } });
                    throw new Error('Session expired. Please sign in again.');
                }

                const { data: retryData, error: retryError } = await supabase.functions.invoke('create-portal-session', { body: { flow } });
                if (retryError) {
                    Sentry.captureException(retryError, { extra: { action: 'createPortalSession.retry' } });
                    throw new Error(retryError.message || 'Failed to create portal session');
                }
                if (retryData?.error) throw new Error(retryData.error);
                if (!retryData?.url) throw new Error('No portal URL returned');

                return retryData.url;
            }
            throw new Error(error.message || 'Failed to create portal session');
        }

        if (data?.error) {
            if (data.error.includes('No active subscription')) {
                throw new Error('You need an active subscription to manage billing. Please subscribe first.');
            }
            throw new Error(data.error);
        }

        if (!data?.url) throw new Error('No portal URL returned');
        return data.url;
    },

    createCheckoutSession: async (planId: PlanId): Promise<string> => {
        // Validate payload using Zod before sending
        const payload = { plan: planId };
        const validation = CheckoutSessionRequestSchema.safeParse(payload);

        if (!validation.success) {
            Sentry.captureException(validation.error, { extra: { action: 'createCheckoutSession.validation', planId } });
            throw new Error('Invalid plan selected.');
        }

        if (import.meta.env.DEV) console.log('[subscriptionService] Invoking create-checkout-session with planId:', planId);

        await supabase.auth.refreshSession();

        const { data, error } = await supabase.functions.invoke('create-checkout-session', {
            body: validation.data
        });

        if (import.meta.env.DEV) console.log('[subscriptionService] create-checkout-session response:', { data, error });

        if (error) {
            Sentry.captureException(error, { extra: { action: 'createCheckoutSession', planId } });
            throw new Error(error.message || 'Failed to create checkout session');
        }

        if (data?.error) throw new Error(data.error);
        if (!data?.url) throw new Error('No checkout URL returned');

        return data.url;
    },

    verifyCheckoutSession: async (sessionId: string): Promise<{ ok: boolean; planId?: PlanId }> => {
        if (import.meta.env.DEV) console.log('[subscriptionService] Invoking verify-checkout-session with sessionId:', sessionId);

        await supabase.auth.refreshSession();

        const { data, error } = await supabase.functions.invoke('verify-checkout-session', {
            body: { sessionId }
        });

        if (import.meta.env.DEV) console.log('[subscriptionService] verify-checkout-session response:', { data, error });

        if (error) {
            Sentry.captureException(error, { extra: { action: 'verifyCheckoutSession', sessionId } });
            throw new Error(error.message || 'Verification failed');
        }

        if (data?.error) throw new Error(data.error);

        return { ok: true, planId: data?.planId as PlanId | undefined };
    },

    canScrapeWebsite: async (): Promise<{ allowed: boolean; reason?: string; remaining?: number }> => {
        const sub = await subscriptionService.getUserSubscription();

        if (!sub.active) {
            return { allowed: false, reason: 'Website extraction is available on the Business plan.' };
        }

        // Scraping/extraction is Business plan only
        if (sub.planId !== 'business' || sub.limits.scrapesPerMonth === 0) {
            return { allowed: false, reason: 'Website extraction is available on the Business plan. Upgrade to use this feature.' };
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            return { allowed: false, reason: 'Authentication required' };
        }

        const { data: subData } = await supabase
            .from('subscriptions')
            .select('scrapes_used_this_month, scrapes_reset_at')
            .eq('user_id', user.id)
            .maybeSingle();

        if (!subData) {
            return { allowed: false, reason: 'No subscription found. Please subscribe to a plan.' };
        }

        const scrapesUsed = subData.scrapes_used_this_month ?? 0;
        const scrapesLimit = sub.limits.scrapesPerMonth;
        const remaining = Math.max(0, scrapesLimit - scrapesUsed);

        if (scrapesUsed >= scrapesLimit) {
            return {
                allowed: false,
                reason: `You've used all ${scrapesLimit} website extractions this month. Resets ${subData.scrapes_reset_at ? new Date(subData.scrapes_reset_at).toLocaleDateString() : 'next month'}.`,
                remaining: 0
            };
        }

        return { allowed: true, remaining };
    },

    incrementScrapeCount: async (): Promise<void> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Call the database function (uses auth.uid() server-side; no param)
        const { error } = await supabase.rpc('increment_scrape_count');

        if (error) {
            Sentry.captureException(error, { extra: { action: 'incrementScrapeCount' } });
        }
    }
};
